import type { IApiKey } from "@gen/app-query";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useEffect, useMemo } from "react";
import { Controller, useForm } from "react-hook-form";

import { CopyableTextInput } from "~/components/copyable_text_input";
import { PublicSvgIcon } from "~/components/public_svg_icon";

interface ApiKeyEditorProps {
  apiKey?: string;
  isLoading?: boolean;
  canEdit?: boolean;
  onSave?: (data: IApiKey) => void;
}

type ApiKeyFormValues = IApiKey;

export function ApiKeyEditor({
  apiKey,
  isLoading = false,
  canEdit = false,
  onSave,
}: ApiKeyEditorProps) {
  const memoValues = useMemo<ApiKeyFormValues>(
    () => ({ api_key: apiKey ?? "" }),
    [apiKey],
  );

  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty },
  } = useForm<ApiKeyFormValues>({
    defaultValues: memoValues,
  });

  useEffect(() => {
    reset(memoValues);
  }, [memoValues, reset]);

  const onSubmit = (data: ApiKeyFormValues) => {
    if (canEdit && onSave) {
      onSave(data);
    }
  };

  const isDisabled = !isDirty;

  return (
    <Stack component="form" onSubmit={handleSubmit(onSubmit)} spacing={5}>
      <Typography variant="h5SolidDivider">
        <PublicSvgIcon name="vpn_key_alert" /> API Key
      </Typography>
      <Controller
        control={control}
        name="api_key"
        render={({ field }) => (
          <CopyableTextInput
            {...field}
            fullWidth
            placeholder="API Key"
            label="API Key"
            disabled={!canEdit}
            data-testid="api-key-input"
          />
        )}
      />
      {canEdit && (
        <Button
          variant="contained"
          type="submit"
          disabled={isDisabled}
          loading={isLoading}
          size="large"
          data-testid="save-api-key-button"
        >
          Save API Key
        </Button>
      )}
    </Stack>
  );
}
